// src/store/testimonialsSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiClient from "./apiClient"; // centralized Axios instance

const ENDPOINT = "/testimonials/";

/* Helper: convert payload to FormData when file is present */
function toFD(obj) {
  const fd = new FormData();
  Object.entries(obj).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    if (v instanceof File) fd.append(k, v);
    else fd.append(k, v);
  });
  return fd;
}

/* ───────── thunks ───────── */

// Fetch all testimonials
export const fetchTestimonials = createAsyncThunk("testimonials/fetchAll", async (_, { rejectWithValue }) => {
  try {
    const { data } = await apiClient.get(ENDPOINT);
    return data;
  } catch (err) {
    return rejectWithValue(err.response?.data || err.message);
  }
});

// Insert a new testimonial
export const insertTestimonial = createAsyncThunk("testimonials/insert", async (payload, { rejectWithValue }) => {
  try {
    const { data } = await apiClient.post(ENDPOINT, toFD(payload), {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  } catch (err) {
    return rejectWithValue(err.response?.data || err.message);
  }
});

// Update an existing testimonial
export const updateTestimonial = createAsyncThunk("testimonials/update", async (payload, { rejectWithValue }) => {
  try {
    const { data } = await apiClient.put(`${ENDPOINT}${payload.id}/`, toFD(payload), {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  } catch (err) {
    return rejectWithValue(err.response?.data || err.message);
  }
});

// Delete a testimonial
export const deleteTestimonial = createAsyncThunk("testimonials/delete", async (id, { rejectWithValue }) => {
  try {
    await apiClient.delete(`${ENDPOINT}${id}/`);
    return id;
  } catch (err) {
    return rejectWithValue(err.response?.data || err.message);
  }
});

/* ───────── slice ───────── */

const testimonialsSlice = createSlice({
  name: "testimonials",
  initialState: { items: [], loading: false, error: null },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchTestimonials.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTestimonials.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchTestimonials.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(insertTestimonial.fulfilled, (state, action) => {
        state.items.push(action.payload);
      })

      .addCase(updateTestimonial.fulfilled, (state, action) => {
        const index = state.items.findIndex((t) => t.id === action.payload.id);
        if (index !== -1) state.items[index] = action.payload;
      })

      .addCase(deleteTestimonial.fulfilled, (state, action) => {
        state.items = state.items.filter((t) => t.id !== action.payload);
      });
  },
});

export default testimonialsSlice.reducer;
